<script setup lang="ts">
import { computed } from 'vue'
import { NEmpty } from 'naive-ui'
import AgentAmberButton from './AgentAmberButton.vue'
import AgentSessionRow from './AgentSessionRow.vue'
import { groupSessionsByRepo } from './AgentSessionGroups'
import type { AgentSessionSummary } from '../stores/agent'

// The /agent Sessions column: slot usage, the Dispatch / New session
// actions, and every session grouped by repo. Renders both inline (desktop
// sidebar) and inside a drawer (mobile) from one source; the parent owns the
// actual store mutations via emits.
const props = defineProps<{
  sessions: AgentSessionSummary[]
  activeThreadId: string | null
  isEmpty: boolean
  // Global agent-session devcontainer slot pool usage (#1352/#1353) — null
  // while unloaded/unavailable, in which case the indicator just doesn't
  // render. Global count only, no per-session/per-repo breakdown by design.
  slotUsage?: { in_use: number; capacity: number } | null
  // Thread whose stop is in flight (~10s of synchronous teardown) — that
  // row's Stop button blocks a second fire (#1381).
  stoppingThreadId?: string | null
}>()

const emit = defineEmits<{
  open: [threadId: string]
  'new-session': []
  dispatch: []
  rename: [threadId: string, name: string]
  delete: [threadId: string]
  stop: [threadId: string]
  info: [session: AgentSessionSummary]
}>()

const groups = computed(() => groupSessionsByRepo(props.sessions))

// At capacity the next run fails outright with "all slots in use", so the
// line escalates instead of letting a full pool read as fine.
const slotsFull = computed(() => !!props.slotUsage && props.slotUsage.in_use >= props.slotUsage.capacity)
</script>

<template>
  <div class="flex flex-col gap-3" data-testid="agent-session-list">
    <div class="flex flex-col gap-1 px-1.5">
      <h2 class="m-0 text-[15px] font-semibold text-ink">Sessions</h2>
      <p
        v-if="slotUsage"
        class="m-0 text-xs"
        :class="slotsFull ? 'text-status-alert' : 'text-ink-faint'"
        data-testid="agent-slot-usage"
      >
        {{ slotUsage.in_use }} of {{ slotUsage.capacity }} devcontainer slots in use<template v-if="slotsFull">
          — new sessions fail until one frees up</template
        >
      </p>
    </div>

    <div class="flex flex-wrap gap-2">
      <AgentAmberButton class="min-h-10 flex-1" data-testid="agent-dispatch-task" @click="emit('dispatch')">
        Dispatch task
      </AgentAmberButton>
      <button
        v-if="activeThreadId"
        type="button"
        class="min-h-10 rounded-[10px] border border-border-control px-3 text-sm font-medium text-ink hover:bg-surface-hover"
        data-testid="agent-new-session"
        @click="emit('new-session')"
      >
        + New session
      </button>
    </div>

    <NEmpty v-if="isEmpty" size="small" description="No active sessions yet" class="py-2" />
    <div v-else class="flex flex-col gap-2">
      <section v-for="group in groups" :key="group.repo" class="flex flex-col gap-0.5">
        <h3 class="m-0 px-2.5 py-1 font-mono text-xs font-normal text-ink-faint" data-testid="agent-session-group">
          {{ group.repo }}
        </h3>
        <ul class="m-0 flex list-none flex-col gap-0.5 p-0" :aria-label="`${group.repo} sessions`">
          <AgentSessionRow
            v-for="s in group.sessions"
            :key="s.thread_id"
            :session="s"
            :active="s.thread_id === activeThreadId"
            :stopping="stoppingThreadId === s.thread_id"
            @open="emit('open', $event)"
            @rename="(id, name) => emit('rename', id, name)"
            @delete="emit('delete', $event)"
            @stop="emit('stop', $event)"
            @info="emit('info', $event)"
          />
        </ul>
      </section>
    </div>
  </div>
</template>
